import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useRequestUserConfigContext } from '../../../hooks/request'
import { decodeFromBase64, getRelativeURL } from '../../../utils/encoding-utils'
import { dataEventClient, searchClient } from '../../../clients'
import SearchRegionNodeTransport from '../../../transports/search-region-node-transport'
import { useAsyncCallback } from '../../../hooks/async'
import { useSessionEffect } from '../../../hooks/session'
import { ScreenContext } from 'react-components'

// ... imports
import { HeadTransport } from '../../../transports'
import { useSelector, useDispatch } from 'react-redux'
import {
    getInstantBookingFilter,
    getIsFilterLoading,
    filterClick,
    clearSearchRequestId,
    getLastDataPathEncoded,
} from '../../../redux/filters'
import { LIST_VIEW_CONFIG } from '../../../constants/search-result-listing'
import headClient from '../../../clients/head-client'
import { useABVariant } from '../../../utils/hooks'
import { AB_KEY, AB_VARIANT_VERSION } from '../../../common/constants'
import { Dispatch } from '../../../redux/types'
import { FilterItemTransport } from '../../../transports/filters-configuration-transport'

interface UseSearchRegionNodeResult {
    searchRegionNode: SearchRegionNodeTransport | null
    isSearchRegionNodeLoading: boolean
    searchRegionNodeError: Error | null
}

interface UseSearchResultListingHeadTransport {
    head: HeadTransport | null
    isHeadResponseLoading: boolean
    headResponseError: Error | null
}

const SEARCHBAR_URL_PARAMS = ['arrival', 'departure', 'persons', 'kids']

const _getSearchbarParamsSignature = (encodedPath: string): string => {
    if (!encodedPath) {
        return ''
    }

    try {
        const [, query = ''] = decodeFromBase64(encodedPath).split('?')
        const params = new URLSearchParams(query)
        return SEARCHBAR_URL_PARAMS.map(param => params.get(param) ?? '').join('|')
    } catch {
        return ''
    }
}

export const useSearchRegionNode = (): UseSearchRegionNodeResult => {
    const user = useRequestUserConfigContext()
    const lastDataPathEncoded = useSelector(getLastDataPathEncoded)

    const _getSearchRegionNode = useCallback(() => {
        const filter = getRelativeURL(globalThis.location.href)
        return searchClient.getSearchRegionNode({ user, filter }).then(response => response.data)
    }, [user])

    const {
        data,
        isLoading: isSearchRegionNodeLoading,
        error: searchRegionNodeError,
        callback: fetchSearchRegionNode,
    } = useAsyncCallback<SearchRegionNodeTransport>(_getSearchRegionNode)

    useSessionEffect(() => {
        fetchSearchRegionNode()
    }, [fetchSearchRegionNode])

    const searchbarParamsSignature = useMemo(
        () => _getSearchbarParamsSignature(lastDataPathEncoded),
        [lastDataPathEncoded],
    )

    const previousSignatureRef = useRef(searchbarParamsSignature)

    useEffect(() => {
        if (previousSignatureRef.current === searchbarParamsSignature) {
            return
        }

        previousSignatureRef.current = searchbarParamsSignature

        fetchSearchRegionNode()
    }, [fetchSearchRegionNode, searchbarParamsSignature])

    return {
        searchRegionNode: data ?? null,
        isSearchRegionNodeLoading,
        searchRegionNodeError,
    }
}

export const useSearchResultListingHead = (): UseSearchResultListingHeadTransport => {
    const user = useRequestUserConfigContext()
    const _getHeadResponse = useCallback(() => {
        const filter = getRelativeURL(globalThis.location.href)
        return headClient.getSRLHead({ user, filter }).then(response => response.data)
    }, [user])

    const {
        data,
        isLoading: isHeadResponseLoading,
        error: headResponseError,
        callback: fetchHeadResponse,
    } = useAsyncCallback<HeadTransport>(_getHeadResponse)

    useSessionEffect(() => {
        fetchHeadResponse()
    }, [fetchHeadResponse])

    return {
        head: data ?? null,
        isHeadResponseLoading,
        headResponseError,
    }
}

/**
 * Hook that sends a search result listing data event to BFF.
 * Fire-and-forget: triggers on every page load after session is ready.
 */
export const useSendSearchResultListingDataEvent = (): void => {
    const user = useRequestUserConfigContext()

    useSessionEffect(() => {
        const filter = getRelativeURL(globalThis.location.href)
        dataEventClient.sendSearchResultListingEvent({ user, filter }).catch(() => {
            // Fire-and-forget: silently ignore errors
        })
    }, [user])
}

const useGridView = () => {
    const { isMobile, isWideScreen } = useContext(ScreenContext)
    const { isProvidedVariantActive } = useABVariant(AB_KEY.SRL_GRID_VIEW, AB_VARIANT_VERSION.ONE)
    const [showGridView, setShowGridView] = useState(isProvidedVariantActive)

    useEffect(() => {
        if (isMobile) {
            setShowGridView(true)
        } else {
            setShowGridView(isProvidedVariantActive)
        }
    }, [isMobile, isProvidedVariantActive])

    const toggleView = () => setShowGridView(prev => !prev)

    const gridItemsPerRow = isMobile
        ? LIST_VIEW_CONFIG.MOBILE_ITEMS_PER_ROW
        : showGridView
          ? isWideScreen
              ? LIST_VIEW_CONFIG.GRID_ITEMS_PER_ROW
              : LIST_VIEW_CONFIG.TABLET_GRID_ITEMS_PER_ROW
          : LIST_VIEW_CONFIG.LIST_ITEMS_PER_ROW

    return {
        isGridView: showGridView,
        gridItemsPerRow,
        toggleView,
    }
}

export default useGridView

interface UseInstantBookingBannerResult {
    item: FilterItemTransport | null
    isChecked: boolean
    isLoading: boolean
    onToggle: () => void
}

export const useInstantBookingBanner = (): UseInstantBookingBannerResult => {
    const dispatch = useDispatch<Dispatch>()
    const user = useRequestUserConfigContext()
    const item = useSelector(getInstantBookingFilter)
    const isLoading = useSelector(getIsFilterLoading)

    const onToggle = useCallback(() => {
        if (!item) {
            return
        }
        dispatch(clearSearchRequestId())
        dispatch(filterClick(item.encodedLink, false, user))
    }, [dispatch, item, user])

    return {
        item,
        isChecked: item?.checked ?? false,
        isLoading,
        onToggle,
    }
}
